/**
 * Featurebase web-portal single sign-on (SSO).
 *
 * When an engineer opens the Featurebase portal (e.g. the feedback link in
 * the messenger) without being signed in there, Featurebase sends them to
 * our SSO page with a `return_to` link. That page signs them in by sending
 * them back to the portal's JWT login with the same token the messenger
 * already uses, so they never see Featurebase's own login.
 *
 * See: https://help.featurebase.app/articles/0874531-web-portal-single-sign-on-sso-setup
 */

/** Our SSO page. Set this URL (with this env's domain) in Featurebase. */
export const FEATUREBASE_SSO_PATH = '/featurebase/sso'

const RETURN_TO_PARAM = 'return_to'
const PORTAL_JWT_LOGIN_PATH = '/api/v1/auth/access/jwt'

/** The workspace's portal address, e.g. https://acme.featurebase.app. */
export function featurebasePortalOrigin(slug: string): string {
  return `https://${slug}.featurebase.app`
}

/**
 * The `return_to` link Featurebase sent, but only if it points back to our
 * own portal. Anything else (missing, malformed, another site) falls back to
 * the portal's home page, so this page can never send someone elsewhere.
 */
export function safePortalReturnTo(rawReturnTo: string | null, slug: string): string {
  const portalOrigin = featurebasePortalOrigin(slug)
  if (!rawReturnTo) {
    return portalOrigin
  }
  try {
    const returnTo = new URL(rawReturnTo)
    return returnTo.origin === portalOrigin ? returnTo.toString() : portalOrigin
  } catch {
    return portalOrigin
  }
}

/** The portal URL that signs the engineer in and lands them on `returnTo`. */
export function portalJwtLoginUrl(slug: string, jwt: string, returnTo: string): string {
  const loginUrl = new URL(PORTAL_JWT_LOGIN_PATH, featurebasePortalOrigin(slug))
  loginUrl.searchParams.set('jwt', jwt)
  loginUrl.searchParams.set(RETURN_TO_PARAM, returnTo)
  return loginUrl.toString()
}

/** Read `return_to` from the SSO page's own address. */
export function readReturnToParam(search: string): string | null {
  return new URLSearchParams(search).get(RETURN_TO_PARAM)
}
